import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generates A4-sized PDF document with product information and itinerary
 * Uses HTML rendering to properly support Korean characters
 */

// A4 dimensions in mm
  const A4_WIDTH = 210;
  const A4_HEIGHT = 297;
  const MARGIN = 10;
  const PAGE_MARGIN_TOP = 10;
  const PAGE_MARGIN_BOTTOM = 10;
  const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2);
  const CONTENT_HEIGHT = A4_HEIGHT - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM;

  // DOM rendering target widths (px) aligned to A4 at 96dpi (~3.7795 px/mm)
  const DOM_PAGE_PX_WIDTH = 794; // ~210mm * 3.7795
  const DOM_CONTENT_PX_WIDTH = Math.round(DOM_PAGE_PX_WIDTH * (CONTENT_WIDTH / A4_WIDTH)); // ~718px for 190mm content

  // Feature flags / defaults
  const TEXT_AWARE_SPLIT_DEFAULT = false; // set true to prefer text-aware splitting by default

  // Colors - Modern and clean design
  const COLORS = {
    primary: '#111827',
    primaryLight: '#374151',
    secondary: '#6b7280',
    text: '#111827',
    textLight: '#4b5563',
    lightGray: '#f8fafc',
    border: '#e5e7eb',
    borderDark: '#d1d5db',
    white: '#ffffff',
    accent: '#2563eb',
  };

  // Simplified styles for print-friendly minimal theme
  const STYLES = {
    container: `
      width: ${CONTENT_WIDTH}px;
      background: white;
      font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', sans-serif;
      padding: 40px;
      box-sizing: border-box;
    `,
    header: `
      color: ${COLORS.text};
      padding: 0 0 16px 0;
      margin: 0 0 24px 0;
      border-bottom: 1px solid ${COLORS.border};
    `,
    infoSection: `
      margin-bottom: 30px;
    `,
    infoTitle: `
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.text};
      margin-bottom: 8px;
      padding: 0;
    `,
    infoContent: `
      padding: 0;
      line-height:  1.8;
      font-size: 12.5px;
    `,
    contentSection: `
      margin-bottom: 25px;
      page-break-inside: avoid;
    `,
    contentTitle: `
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.text};
      margin-bottom: 8px;
      padding: 0;
      border-bottom: 1px solid ${COLORS.border};
    `,
    contentBody: `
      padding: 0;
      line-height:  1.8;
      color: ${COLORS.text};
      font-size: 12.5px;
      white-space: pre-wrap;
    `,
    itineraryTitle: `
      font-size: 16px;
      font-weight: 700;
      color: ${COLORS.text};
      margin: 24px 0 12px 0;
      padding: 0 0 8px;
      border-bottom: 1px solid ${COLORS.border};
    `,
    daySection: `
      margin-bottom: 25px;
      page-break-inside: avoid;
    `,
    dayHeader: `
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.text};
      margin-bottom: 8px;
    `,
    scheduleItem: `
      background: ${COLORS.white};
      padding: 8px 10px;
      margin-bottom: 8px;
      border: 1px solid ${COLORS.border};
      border-radius: 4px;
    `,
    scheduleName: `
      font-weight: 600;
      color: ${COLORS.text};
      margin-bottom: 6px;
      font-size: 13px;
    `,
    scheduleContent: `
      color: ${COLORS.text};
      line-height: 1;
      font-size: 12.5px;
      margin-bottom: 6px;
      white-space: pre-wrap;
    `,
    flightDiv: `
      color: ${COLORS.secondary};
      font-size: 11.5px;
      margin-top: 4px;
    `,
  };

/**
 * Create HTML element for rendering (Table-based layout for print)
 */
function createHTMLElement(contentMainList, itineraryData, productInfo) {
  const container = document.createElement('div');
  container.style.cssText = `
    width: 100%;
    background: white;
    font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', sans-serif;
    padding: 10px;
    box-sizing: border-box;
    font-size: 12px;
  `;

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Sanitize and preserve basic HTML formatting (paragraphs, lists, bold, italics, line breaks)
  const sanitizeBasicHtml = (html) => {
    if (!html) return '';
    const template = document.createElement('template');
    template.innerHTML = html;

    const allowed = new Set([
      'BR','P','DIV','UL','OL','LI','STRONG','B','EM','I','U','TABLE','THEAD','TBODY','TR','TD','TH','HR','SPAN',
      'H1','H2','H3','H4','H5','H6','BLOCKQUOTE','A','IMG'
    ]);

    const cleanNode = (node) => {
      // Text node
      if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode(node.nodeValue);
      }

      // Element node
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toUpperCase();
        const cls = typeof node.getAttribute === 'function' ? (node.getAttribute('class') || '') : '';
        const textLig = (node.textContent || '').trim();
        const ligatureMap = {
          expand_more: '▾',
          expand_less: '▴',
          chevron_right: '›',
          chevron_left: '‹',
          arrow_forward: '→',
          arrow_back: '←',
          place: '📍',
          location_on: '📍',
          restaurant: '🍽',
          calendar_today: '📅',
          phone: '☎',
          call: '☎',
          email: '✉',
          star: '★',
          star_border: '☆',
          check: '✓',
          done: '✓',
          close: '✕',
          clear: '✕',
        };
        if (/material-icons|material-symbols/i.test(cls)) {
          const mapped = ligatureMap[textLig] || '';
          return mapped ? document.createTextNode(mapped) : document.createDocumentFragment();
        }
        const classIconMap = [
          [/(fa-phone|mdi-phone)/i, '☎'],
          [/(fa-envelope|mdi-email)/i, '✉'],
          [/(fa-map-marker|mdi-map-marker)/i, '📍'],
          [/(fa-calendar|mdi-calendar)/i, '📅'],
          [/(fa-star)/i, '★'],
          [/(fa-check|mdi-check)/i, '✓'],
          [/(fa-times|fa-close|mdi-close)/i, '✕'],
          [/(fa-chevron-right|mdi-chevron-right)/i, '›'],
          [/(fa-chevron-left|mdi-chevron-left)/i, '‹'],
        ];
        for (const [re, ch] of classIconMap) {
          if (re.test(cls)) return document.createTextNode(ch);
        }
        // Drop script/style entirely
        if (tag === 'SCRIPT' || tag === 'STYLE') {
          return document.createDocumentFragment();
        }

        // If tag not allowed, unwrap children
        if (!allowed.has(tag)) {
          const frag = document.createDocumentFragment();
          node.childNodes.forEach((c) => frag.appendChild(cleanNode(c)));
          return frag;
        }

        // Recreate allowed element, copying only safe attributes for certain tags
        const tagLower = tag.toLowerCase();
        const el = document.createElement(tagLower);
        // Copy safe attributes
        const copyAttr = (name) => {
          const val = node.getAttribute(name);
          if (val != null) el.setAttribute(name, val);
        };
        // Preserve whitelisted classes to support limited styling
        const allowClasses = new Set([
          'material-icons','material-icons-round','material-symbols',
          'day-section','day-title','day-content','schedule-item','toggle-icon','icon','location',
          'meals','meal-info','meal-item','product-info','info-section','detail-text','detail-row','itinerary-section',
          'info-table','__se_tbl'
        ]);
        const classAttr = node.getAttribute('class') || '';
        if (classAttr) {
          const filtered = classAttr.split(/\s+/).filter(c => allowClasses.has(c));
          if (filtered.length) el.setAttribute('class', filtered.join(' '));
        }
        if (tagLower === 'img') {
          // allow http/https/data images
          const src = node.getAttribute('src') || '';
          if (/^(https?:|data:image\/)/i.test(src)) el.setAttribute('src', src);
          const alt = node.getAttribute('alt');
          if (alt != null) el.setAttribute('alt', alt);
        } else if (tagLower === 'a') {
          const href = node.getAttribute('href') || '';
          if (/^(https?:|mailto:|tel:|#)/i.test(href)) el.setAttribute('href', href);
        }
        node.childNodes.forEach((c) => el.appendChild(cleanNode(c)));
        return el;
      }

      // Other node types: ignore
      return document.createDocumentFragment();
    };

    const container = document.createElement('div');
    template.content.childNodes.forEach((n) => container.appendChild(cleanNode(n)));
    // Ensure raw newlines render even when no tags are present
    return container.innerHTML.replace(/\n/g, '<br/>');
  };

  // Header - Simple product name
  const header = document.createElement('div');
  header.style.cssText = `
    color: ${COLORS.text};
    padding: 0 0 12px 0;
    margin: 0 0 16px 0;
    border-bottom: 2px solid ${COLORS.text};
  `;
  header.setAttribute('data-section', 'header');
  header.innerHTML = `
    <h1 style="margin: 0; font-size: 18px; font-weight: 700;">${productInfo?.상품명 || '여행 상품'}</h1>
  `;
  container.appendChild(header);

  // Content Sections - Table format like the reference image
  // Group content by 코드명 (category)
  if (Array.isArray(contentMainList) && contentMainList.length > 0) {
    const contentTable = document.createElement('table');
    contentTable.style.cssText = `
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      font-size: 12px;
    `;
    contentTable.setAttribute('data-break-candidate', '1');

    contentMainList.forEach(content => {
      const row = document.createElement('tr');
      row.setAttribute('data-break-candidate', '1');

      // 코드명 (Category) cell - left header
      const thCell = document.createElement('th');
      thCell.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 10px;
        background: #f9fafb;
        font-weight: 600;
        text-align: left;
        vertical-align: top;
        width: 100px;
        min-width: 100px;
        font-size: 12px;
      `;
      thCell.textContent = content.코드명 || content.콘텐츠명 || '상세 정보';
      row.appendChild(thCell);

      // Content cell - right content
      const tdCell = document.createElement('td');
      tdCell.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 10px;
        vertical-align: top;
        line-height: 1.6;
        font-size: 12px;
      `;

      // Content name as title if different from 코드명
      const contentName = content.콘텐츠명 || '';
      const codeName = content.코드명 || '';
      let contentHtml = '';
      if (contentName && contentName !== codeName) {
        contentHtml += `<div style="font-weight: 600; margin-bottom: 4px;">${contentName}</div>`;
      }
      // Add the actual content (태그/HTML)
      if (content.콘텐츠내용) {
        contentHtml += sanitizeBasicHtml(content.콘텐츠내용);
      }
      tdCell.innerHTML = contentHtml;

      // Style nested tables and images
      try {
        const tbls = tdCell.querySelectorAll('table');
        tbls.forEach((t) => {
          t.style.width = '100%';
          t.style.borderCollapse = 'collapse';
          t.style.margin = '4px 0';
          const ths = t.querySelectorAll('th');
          const tds = t.querySelectorAll('td');
          ths.forEach(th => {
            th.style.border = `1px solid ${COLORS.border}`;
            th.style.padding = '4px 6px';
            th.style.background = '#f9fafb';
            th.style.fontWeight = '600';
            th.style.textAlign = 'left';
            th.style.fontSize = '11px';
          });
          tds.forEach(td => {
            td.style.border = `1px solid ${COLORS.border}`;
            td.style.padding = '4px 6px';
            td.style.verticalAlign = 'top';
            td.style.fontSize = '11px';
          });
        });
        const imgs = tdCell.querySelectorAll('img');
        imgs.forEach(img => {
          if (img.closest('table')) {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.display = 'block';
            return;
          }
          img.style.width = 'calc(25% - 4px)';
          img.style.maxWidth = 'calc(25% - 4px)';
          img.style.height = 'auto';
          img.style.display = 'inline-block';
          img.style.verticalAlign = 'top';
          img.style.margin = '2px';
          img.style.borderRadius = '4px';
        });
      } catch (_) {}

      row.appendChild(tdCell);
      contentTable.appendChild(row);
    });

    container.appendChild(contentTable);
  }

  // Itinerary Section - Table format like the reference image
  // Structure: 일차 | 일정 (호텔 정보 별도) | 식사 (조/중/석 줄바꿈)
  if (itineraryData && itineraryData.table1 && itineraryData.table1.length > 0) {
    // Itinerary header
    const itineraryHeader = document.createElement('div');
    itineraryHeader.style.cssText = `
      font-size: 14px;
      font-weight: 700;
      color: ${COLORS.text};
      margin: 16px 0 8px 0;
      padding: 0 0 6px;
      border-bottom: 2px solid ${COLORS.text};
    `;
    itineraryHeader.textContent = '일정표';
    container.appendChild(itineraryHeader);

    // Create itinerary table
    const itineraryTable = document.createElement('table');
    itineraryTable.style.cssText = `
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      font-size: 12px;
    `;

    // Table header row
    const headerRow = document.createElement('tr');
    const headers = ['일차', '일정', '식사'];
    const headerWidths = ['60px', 'auto', '100px'];
    headers.forEach((text, idx) => {
      const th = document.createElement('th');
      th.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 10px;
        background: #e5e7eb;
        font-weight: 700;
        text-align: center;
        vertical-align: middle;
        font-size: 12px;
        ${headerWidths[idx] !== 'auto' ? `width: ${headerWidths[idx]}; min-width: ${headerWidths[idx]};` : ''}
      `;
      th.textContent = text;
      headerRow.appendChild(th);
    });
    itineraryTable.appendChild(headerRow);

    // Group by day
    const schedulesByDay = {};
    itineraryData.table1.forEach(schedule => {
      const day = schedule.일차 || schedule.dayNo || 1;
      if (!schedulesByDay[day]) schedulesByDay[day] = [];
      schedulesByDay[day].push(schedule);
    });

    const days = Object.keys(schedulesByDay).sort((a, b) => Number(a) - Number(b));

    // Helper functions
    const getDayNo = (row) => Number(row?.일차 ?? row?.dayNo ?? row?.DayNo ?? row?.day ?? row?.일자 ?? 0);
    // 호텔 정보 추출: 자원명 + 가격옵션명 또는 일정명 조합
    const getHotelDisplayName = (row) => {
      const resourceName = String(row?.자원명 || '').trim();
      const priceOptionName = String(row?.가격옵션명 || '').trim();
      const scheduleName = String(row?.일정명 || '').trim();
      const hotelName = row?.호텔명 || row?.hotelName || row?.숙소명 || '';
      
      // 우선순위: 자원명 > 호텔명 > 일정명
      let displayName = resourceName || hotelName || scheduleName;
      
      // 가격옵션명이 있으면 추가
      if (displayName && priceOptionName && priceOptionName !== displayName) {
        displayName = `${displayName} (${priceOptionName})`;
      } else if (!displayName && priceOptionName) {
        displayName = priceOptionName;
      }
      
      return displayName;
    };

    days.forEach(day => {
      const schedules = schedulesByDay[day];
      const dayRow = document.createElement('tr');
      dayRow.setAttribute('data-break-candidate', '1');

      // 일차 cell
      const dayCell = document.createElement('td');
      dayCell.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 6px;
        text-align: center;
        vertical-align: top;
        font-weight: 700;
        font-size: 12px;
        background: #f9fafb;
      `;
      dayCell.innerHTML = `${day}일차`;
      dayRow.appendChild(dayCell);

      // 일정 cell - schedules + hotel info
      const scheduleCell = document.createElement('td');
      scheduleCell.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 10px;
        vertical-align: top;
        line-height: 1.5;
        font-size: 11px;
      `;

      // Build schedule content
      let scheduleHtml = '';
      schedules.forEach((schedule, idx) => {
        const isFlightSchedule = schedule.항공여부 || schedule.isFlight;
        const prefix = isFlightSchedule ? '✈ ' : '• ';
        const scheduleName = schedule.일정명 || schedule.scheduleName || `일정 ${idx + 1}`;
        const scheduleContent = schedule.일정내용 || schedule.content || '';

        // Schedule name with bullet
        scheduleHtml += `<div style="margin-bottom: 4px;">`;
        scheduleHtml += `<span style="font-weight: 600;">${prefix}${scheduleName}</span>`;
        scheduleHtml += `</div>`;

        // Schedule content (if exists)
        if (scheduleContent) {
          const cleanContent = sanitizeBasicHtml(scheduleContent);
          scheduleHtml += `<div style="margin-left: 12px; margin-bottom: 6px; color: ${COLORS.textLight};">${cleanContent}</div>`;
        }

        // Flight info
        if (isFlightSchedule && itineraryData.table5) {
          const flightInfo = itineraryData.table5.find(f =>
            f.상품일정번호 === schedule.상품일정번호 || f.scheduleNo === schedule.scheduleNo
          );
          if (flightInfo) {
            const flightText = [
              flightInfo.편명 || flightInfo.flightNo,
              flightInfo.출발공항 || flightInfo.depAirport,
              '→',
              flightInfo.도착공항 || flightInfo.arrAirport,
            ].filter(Boolean).join(' ');
            if (flightText) {
              scheduleHtml += `<div style="margin-left: 12px; margin-bottom: 6px; color: ${COLORS.secondary}; font-size: 10px;">${flightText}</div>`;
            }
          }
        }
      });

      // Hotel info - separate section at the bottom (from table2)
      try {
        const t2 = Array.isArray(itineraryData?.table2) ? itineraryData.table2 : [];
        const dayHotels = t2.filter(r => getDayNo(r) === Number(day));
        
        if (dayHotels.length > 0) {
          // Group by 요금제명
          const groupsMap = new Map();
          for (const h of dayHotels) {
            const tariff = String(h?.요금제명 || '').trim() || '기본';
            if (!groupsMap.has(tariff)) groupsMap.set(tariff, []);
            groupsMap.get(tariff).push(h);
          }
          
          const hotelNames = [];
          for (const [tariffName, items] of groupsMap) {
            for (const h of items) {
              const displayName = getHotelDisplayName(h);
              if (displayName) {
                hotelNames.push(displayName);
              }
            }
          }
          
          const uniqueHotels = Array.from(new Set(hotelNames.filter(Boolean)));
          if (uniqueHotels.length > 0) {
            scheduleHtml += `<div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed ${COLORS.border};">`;
            scheduleHtml += `<span style="font-weight: 600; color: ${COLORS.accent};">예정호텔:</span> ${uniqueHotels.join(', ')}`;
            scheduleHtml += `</div>`;
          }
        }
      } catch (_) {}

      scheduleCell.innerHTML = scheduleHtml;
      dayRow.appendChild(scheduleCell);

      // 식사 cell - 조/중/석 줄바꿈
      const mealCell = document.createElement('td');
      mealCell.style.cssText = `
        border: 1px solid ${COLORS.borderDark};
        padding: 8px 6px;
        vertical-align: top;
        text-align: left;
        font-size: 11px;
        line-height: 1.6;
      `;

      // Get meals from table3 or from schedule items
      let mealHtml = '';
      try {
        const t3 = Array.isArray(itineraryData?.table3) ? itineraryData.table3 : [];
        const mealRow = t3.find(r => getDayNo(r) === Number(day));
        
        if (mealRow) {
          // Parse meal info from table3
          const breakfast = mealRow.조식 || mealRow.breakfast || '';
          const lunch = mealRow.중식 || mealRow.lunch || '';
          const dinner = mealRow.석식 || mealRow.dinner || '';
          
          if (breakfast) mealHtml += `<div>[조식]${breakfast}</div>`;
          if (lunch) mealHtml += `<div>[중식]${lunch}</div>`;
          if (dinner) mealHtml += `<div>[석식]${dinner}</div>`;
          
          // Fallback to 식사정보 if individual fields are empty
          if (!mealHtml && mealRow.식사정보) {
            const parts = String(mealRow.식사정보).split(/\s*\/\s*/);
            if (parts[0] && parts[0] !== '미정') mealHtml += `<div>[조식]${parts[0]}</div>`;
            if (parts[1] && parts[1] !== '미정') mealHtml += `<div>[중식]${parts[1]}</div>`;
            if (parts[2] && parts[2] !== '미정') mealHtml += `<div>[석식]${parts[2]}</div>`;
          }
        }
        
        // Fallback: get from schedule items
        if (!mealHtml) {
          const allMeals = { breakfast: '', lunch: '', dinner: '' };
          schedules.forEach(schedule => {
            if (schedule.조식 || schedule.breakfast) allMeals.breakfast = schedule.조식 || schedule.breakfast;
            if (schedule.중식 || schedule.lunch) allMeals.lunch = schedule.중식 || schedule.lunch;
            if (schedule.석식 || schedule.dinner) allMeals.dinner = schedule.석식 || schedule.dinner;
          });
          if (allMeals.breakfast) mealHtml += `<div>[조식]${allMeals.breakfast}</div>`;
          if (allMeals.lunch) mealHtml += `<div>[중식]${allMeals.lunch}</div>`;
          if (allMeals.dinner) mealHtml += `<div>[석식]${allMeals.dinner}</div>`;
        }
      } catch (_) {}

      mealCell.innerHTML = mealHtml || '-';
      dayRow.appendChild(mealCell);

      itineraryTable.appendChild(dayRow);
    });

    container.appendChild(itineraryTable);
  }

  // Footer
  const footer = document.createElement('div');
  footer.style.cssText = `
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid ${COLORS.border};
    text-align: center;
    color: ${COLORS.secondary};
    font-size: 12px;
  `;
  footer.setAttribute('data-section', 'footer');
  footer.textContent = `생성일: ${new Date().toLocaleDateString('ko-KR')}`;
  container.appendChild(footer);

  return container;
}

/**
 * Render HTML element to canvas
 */
async function renderToCanvas(element) {
  return await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: 794,
    imageTimeout: 8000,
    removeContainer: true,
    // foreignObjectRendering can be slower/heavier; keep default false for stability
  });
}

/**
 * Add canvas to PDF with proper positioning
 */
function addCanvasToPDF(pdf, canvas, yPosition) {
  const imgData = canvas.toDataURL('image/jpeg', 0.75);
  const imgWidth = A4_WIDTH;
  const imgHeight = (canvas.height * A4_WIDTH) / canvas.width;
  
  pdf.addImage(imgData, 'JPEG', 0, yPosition, imgWidth, imgHeight, undefined, 'FAST');
  return imgHeight;
}

/**
 * Add a long canvas to the PDF by slicing it into multiple A4-height pages
 * with proper margins and consistent scaling.
 */
async function addCanvasAsPages(pdf, canvas) {
  const targetWidthMM = A4_WIDTH - (MARGIN * 2);
  const pageHeightMM = A4_HEIGHT - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM;
  const pxPerMm = canvas.width / targetWidthMM; // how many pixels correspond to 1mm at the chosen width
  const pageHeightPx = Math.floor(pageHeightMM * pxPerMm);

  let y = 0;
  let pageIndex = 0;
  while (y < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - y);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const ctx = sliceCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0, y, canvas.width, sliceHeightPx, // source rect
      0, 0, canvas.width, sliceHeightPx  // dest rect
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const imgWidthMM = targetWidthMM;
    const imgHeightMM = (sliceHeightPx / canvas.width) * imgWidthMM;

    if (pageIndex > 0) {
      pdf.addPage();
    }
    pdf.addImage(
      imgData,
      'PNG',
      MARGIN,
      PAGE_MARGIN_TOP,
      imgWidthMM,
      imgHeightMM,
      undefined,
      'FAST'
    );

    y += sliceHeightPx;
    pageIndex += 1;

    // Page guard and cooperative yield
    if (pageIndex > Math.ceil(canvas.height / Math.max(1, pageHeightPx)) + 10) {
      break;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0));
  }
}

/**
 * Collect DOM break candidates (bottom Y positions in DOM px) to avoid cutting through text.
 * Includes our explicit data markers and common block/text elements.
 */
function collectBreakCandidateDomYs(element) {
  const selectors = [
    '[data-break-candidate]','p','li','table','tr','td','th','hr','br'
  ];
  const rootRect = element.getBoundingClientRect();
  const nodes = element.querySelectorAll(selectors.join(','));
  const ys = [];

  // 1) Block-level and marked elements
  nodes.forEach((n) => {
    const r = n.getBoundingClientRect();
    const yTop = r.top - rootRect.top;
    if (isFinite(yTop) && yTop > 0 && yTop <= rootRect.height) ys.push(yTop);
  });

  // 2) Line-level text rects (per visual line)
  try {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!node || !node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          const p = node.parentElement;
          if (!p) return NodeFilter.FILTER_REJECT;
          if (p.closest('script,style')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    let tn;
    while ((tn = walker.nextNode())) {
      const range = document.createRange();
      range.selectNodeContents(tn);
      const rects = range.getClientRects();
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        // prefer line top to avoid mid-line cuts
        const yTop = r.top - rootRect.top;
        if (isFinite(yTop) && yTop > 0 && yTop <= rootRect.height) ys.push(yTop);
      }
    }
  } catch (_) {
    // Fallback silently if TreeWalker/Range fails in the environment
  }

  // De-duplicate and sort asc
  return Array.from(new Set(ys.map(v => Math.round(v)))).sort((a,b) => a-b);
}

/**
 * Text-aware page slicing: align page cuts to nearest candidate boundary below the target
 * to reduce cutting through lines/boxes.
 */
async function addCanvasAsPagesTextAware(pdf, canvas, element, options = {}) {
  const { useWhiteScan = false } = options;
  const targetWidthMM = A4_WIDTH - (MARGIN * 2);
  const pageHeightMM = A4_HEIGHT - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM;
  const pxPerMm = canvas.width / targetWidthMM;
  const pageHeightPx = Math.floor(pageHeightMM * pxPerMm);

  // Map DOM px -> canvas px using horizontal scale
  const rect = element.getBoundingClientRect();
  const domToCanvasX = canvas.width / rect.width;
  const domToCanvasY = canvas.height / rect.height;
  const candidateDomYs = collectBreakCandidateDomYs(element);
  const candidateCanvasYs = candidateDomYs.map(y => Math.round(y * domToCanvasY));
  candidateCanvasYs.push(canvas.height); // ensure final boundary
  candidateCanvasYs.sort((a,b) => a-b);

  let y = 0;
  let pageIndex = 0;
  const minSlicePx = Math.max(60, Math.floor(24 * domToCanvasY)); // avoid tiny slices
  const CUT_SAFETY = Math.max(2, Math.round(2 * domToCanvasY)); // cut slightly above a line to avoid clipping
  const OVERLAP_PX = Math.max(4, Math.round(4 * domToCanvasY)); // duplicate a tiny band across pages

  // Optional: whiteness-based fine cut finder (CPU-heavy). Disabled by default.
  let findSafeCutNear = (y) => y;
  if (useWhiteScan) {
    try {
      const ctx = canvas.getContext('2d');
      const bandX = Math.floor(canvas.width * 0.2);
      const bandW = Math.max(8, Math.floor(canvas.width * 0.6));
      const rowWhiteRatio = (y) => {
        y = Math.max(0, Math.min(canvas.height - 1, y|0));
        const data = ctx.getImageData(bandX, y, bandW, 1).data;
        let white = 0;
        const total = bandW;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
          if (a < 8 || (r > 245 && g > 245 && b > 245)) white++;
        }
        return white / total;
      };
      const rowEdgeEnergy = (y) => {
        y = Math.max(1, Math.min(canvas.height - 1, y|0));
        const row = ctx.getImageData(bandX, y, bandW, 1).data;
        const prev = ctx.getImageData(bandX, y-1, bandW, 1).data;
        let acc = 0;
        const total = bandW;
        for (let i = 0; i < row.length; i += 4) {
          const r = row[i], g = row[i+1], b = row[i+2], a = row[i+3];
          const rp = prev[i], gp = prev[i+1], bp = prev[i+2], ap = prev[i+3];
          const lum = (a < 8) ? 255 : (0.2126*r + 0.7152*g + 0.0722*b);
          const lump = (ap < 8) ? 255 : (0.2126*rp + 0.7152*gp + 0.0722*bp);
          acc += Math.abs(lum - lump);
        }
        return acc / total;
      };
      findSafeCutNear = (target, windowPx = Math.max(16, Math.round(18 * domToCanvasY))) => {
        let bestY = target;
        let bestWhite = -1;
        let bestEdge = Infinity;
        const start = Math.max(0, target - windowPx);
        const end = Math.min(canvas.height - 1, target + windowPx);
        for (let y0 = start; y0 <= end; y0++) {
          const white = rowWhiteRatio(y0);
          const edge = rowEdgeEnergy(y0);
          const betterWhite = white > bestWhite + 0.005;
          const similarWhite = Math.abs(white - bestWhite) <= 0.005;
          const betterEdge = edge < bestEdge - 0.5;
          if (betterWhite || (similarWhite && betterEdge)) {
            bestWhite = white;
            bestEdge = edge;
            bestY = y0;
            if (white >= 0.992 && edge <= 2) break;
          }
        }
        return bestY;
      };
    } catch (e) {
      // On tainted canvas or security errors, fallback to no-op
      findSafeCutNear = (y) => y;
    }
  }

  while (y < canvas.height) {
    const target = y + pageHeightPx - (pageIndex > 0 ? OVERLAP_PX : 0);
    // pick the largest candidate <= target, but > y + minSlicePx
    let cut = null;
    for (let i = candidateCanvasYs.length - 1; i >= 0; i--) {
      const cy = candidateCanvasYs[i];
      if (cy <= target && cy > y + minSlicePx) {
        cut = Math.max(y + minSlicePx, cy - CUT_SAFETY);
        break;
      }
    }
    if (!cut) {
      // if none found within page, fallback to target or end
      cut = Math.min(target, canvas.height);
      if (cut <= y) {
        // safety to prevent infinite loop
        cut = Math.min(y + pageHeightPx, canvas.height);
      }
    }

    // Final adjust cut to nearest white row to avoid slicing mid-text
    cut = findSafeCutNear(cut);
    // Enforce forward progress and minimum slice height
    const minAdvance = Math.max(minSlicePx, 8);
    if (cut < y + minAdvance) {
      cut = Math.min(y + pageHeightPx, canvas.height);
      if (cut <= y) break; // last resort to avoid infinite loop
    }
    const sliceStart = pageIndex > 0 ? Math.max(0, y - OVERLAP_PX) : y;
    const sliceHeightPx = cut - sliceStart;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const sliceCtx = sliceCanvas.getContext('2d');
    sliceCtx.drawImage(
      canvas,
      0, sliceStart, canvas.width, sliceHeightPx,
      0, 0, canvas.width, sliceHeightPx
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const imgWidthMM = targetWidthMM;
    const imgHeightMM = (sliceHeightPx / canvas.width) * imgWidthMM;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(
      imgData,
      'PNG',
      MARGIN,
      PAGE_MARGIN_TOP,
      imgWidthMM,
      imgHeightMM,
      undefined,
      'FAST'
    );

    y = cut;
    pageIndex += 1;

    // Page guard to avoid infinite loops on unexpected conditions
    if (pageIndex > Math.ceil(canvas.height / Math.max(1, minSlicePx)) + 10) {
      break;
    }

    // Yield to UI to avoid long blocking
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0));
  }
}

/**
 * Export product details and itinerary to PDF - Optimized for fewer pages and better readability
 * @param {Object} params
 * @param {Array} params.contentMainList - Product content details from DBO.RCT_S_콘텐츠메인리스트
 * @param {Object} params.itineraryData - Itinerary data from DBO.RCT_S_상품일정_02
 * @param {Object} params.productInfo - Basic product information
 */
export async function exportTravelProductPDF({ contentMainList, itineraryData, productInfo }) {
  try {
    const productName = productInfo?.상품명 || '여행 상품';

    // Create PDF with maximum compression
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    let currentY = PAGE_MARGIN_TOP;
    let isFirstPage = true;

    // Helper function to add section to PDF (with proper page splitting)
    const addSectionToPDF = async (element) => {
      // Temporarily add to DOM (hidden)
      element.style.position = 'fixed';
      element.style.left = '-9999px';
      element.style.top = '0';
      document.body.appendChild(element);

      try {
        console.time('pdf:render');
        // Wait a tick for layout
        await new Promise(resolve => setTimeout(resolve, 50));

        // Convert to canvas with timeout and fallback
        const RENDER_TIMEOUT_MS = 20000;
        let canvas;
        try {
          canvas = await Promise.race([
            renderToCanvas(element),
            new Promise((_, reject) => setTimeout(() => reject(new Error('html2canvas timeout')), RENDER_TIMEOUT_MS))
          ]);
        } catch (e) {
          console.warn('Primary render failed or timed out, retrying with lighter settings...', e);
          canvas = await html2canvas(element, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            windowWidth: 794,
            imageTimeout: 4000,
            removeContainer: true,
          });
        }
        console.timeEnd('pdf:render');

        console.time('pdf:slice');
        if (TEXT_AWARE_SPLIT_DEFAULT) {
          try {
            const SLICE_TIMEOUT_MS = 12000;
            await Promise.race([
              addCanvasAsPagesTextAware(pdf, canvas, element),
              new Promise((_, reject) => setTimeout(() => reject(new Error('slice timeout')), SLICE_TIMEOUT_MS))
            ]);
          } catch (sliceErr) {
            console.warn('Text-aware slicing failed or timed out, falling back to simple slicing...', sliceErr);
            await addCanvasAsPages(pdf, canvas);
          }
        } else {
          await addCanvasAsPages(pdf, canvas);
        }
        console.timeEnd('pdf:slice');
      } finally {
        // Remove from DOM after measuring
        try { document.body.removeChild(element); } catch (_) {}
      }
    };

    // Create single HTML element containing all content
    const element = createHTMLElement(contentMainList, itineraryData, productInfo);
    await addSectionToPDF(element);

    // Save PDF
    const fileName = `${productName}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    return fileName;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

/**
 * Open print preview window with product details and itinerary.
 * Users can print or save as PDF using browser's native print dialog.
 */
export async function openTravelProductPrintPreview({ contentMainList, itineraryData, productInfo }) {
  try {
    const el = createHTMLElement(contentMainList, itineraryData, productInfo);

    const productName = (productInfo?.상품명 || '여행 상품').replace(/[\"<>:|?*]/g, ' ');
    const today = new Date().toISOString().split('T')[0];

    const win = window.open('', '_blank');
    if (!win) throw new Error('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.');

    const html = `<!DOCTYPE html>
<html lang="ko">
  <head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${productName} - 인쇄 미리보기</title>
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons+Round" rel="stylesheet">
  <style>
    html, body { height: 100%; }
    body { margin: 0; background: #f3f4f6; font-family: 'Malgun Gothic','맑은 고딕','Apple SD Gothic Neo',sans-serif; color: #111827; }
    .toolbar { position: sticky; top: 0; z-index: 100; background: #ffffff; border-bottom: 1px solid #e5e7eb; padding: 10px 12px; display: flex; gap: 8px; align-items: center; }
    .toolbar-title { font-weight: 600; font-size: 14px; margin-right: auto; color: #111827; }
    .btn { appearance: none; border: 1px solid #d1d5db; background: #ffffff; color: #111827; padding: 6px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; }
    .btn.primary { background: #2563eb; color: #ffffff; border-color: #2563eb; }
    .btn:hover { filter: brightness(0.98); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .sheet { width: 210mm; min-height: 297mm; margin: 12px auto; background: #ffffff; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 1px 6px rgba(0,0,0,0.08); }
    .content { padding: 0; }
    /* Scoped itinerary styles (print-friendly approximation) */
    .itinerary-section, .itinerary-section * { line-height: 1.2 !important; }
    .day-section { margin-bottom: 18px; background: #fff; border-radius: 6px; overflow: hidden; border: 1px solid #d1d5db; }
    .day-title { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgb(14,37,59); color: #fff; font-size: 14px; font-weight: 700; }
    .day-content { padding: 14px; background: #fff; position: relative; line-height: 1; }
    .day-content.collapsed { display: none; }
    .schedule-item { margin: 0px 0; display: flex; align-items: flex-start; padding: 8px; border-radius: 8px; position: relative; border: 1px solid #e5e7eb; }
    .schedule-item:not(:last-child)::after { content: none; }
    .icon { width: 24px; height: 24px; margin-right: 8px; display: flex; align-items: center; justify-content: center; color: #111827; }
    .material-icons-round { font-size: 18px; line-height:  1.5; }
    .toggle-icon { transition: transform 0.2s ease; }
    .toggle-icon.collapsed { transform: rotate(-180deg); }
    .location { font-size: 13px; font-weight: 700; margin-bottom: 4px; }
    .meals { margin-top: 12px; padding: 8px 12px; background: #f7f7f7; border-radius: 8px; }
    .meal-info { display: flex; gap: 8px; whitespace: nowrap; }
    .meal-item { background: #fff; padding: 4px 12px; border-radius: 14px; font-size: 12px; color: #111827; border: 1px solid #d1d5db; }
    .info-section { background: #fff; border-radius: 10px; padding: 14px; font-size: 12px; margin-bottom: 14px; border: 1px solid #d1d5db; }
    .product-info { width: 100%; max-width: 100%; overflow-x: visible; }
    @media print {
      @page { size: A4; margin: 10mm; }
      html, body { height: auto; }
      body { background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .toolbar { display: none !important; }
      .sheet { box-shadow: none !important; margin: 0 !important; width: 210mm !important; min-height: 297mm !important; }
      .content { padding: 0 !important; }
      /* Keep product info/content readable */
      .content .info-section, .content .product-info, .content .content-body { line-height: 1.8 !important; }
      /* Force compact itinerary */
      .itinerary-section, .itinerary-section * { line-height: 0.7 !important; }
      .day-content { line-height: 0.7 !important; }
    }
  </style>
  </head>
  <body>
    <div class="toolbar">
      <div class="toolbar-title">${productName} · 미리보기 (${today})</div>
      <button class="btn" onclick="window.close()">닫기</button>
      <button class="btn primary" onclick="window.print()">인쇄</button>
    </div>
    <div class="sheet">
      <div class="content">${el.outerHTML}</div>
    </div>
  </body>
</html>`;

    win.document.open();
    win.document.write(html);
    win.document.close();
    try { win.focus(); } catch (_) {}
  } catch (error) {
    console.error('Print preview error:', error);
    throw error;
  }
}

export async function generatePdfBlobFromElement(element, options = {}) {
  if (!element) throw new Error('PDF 생성 대상 요소를 찾을 수 없습니다.');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

  element.style.position = 'fixed';
  element.style.left = '-9999px';
  element.style.top = '0';
  document.body.appendChild(element);

  try {
    await new Promise((resolve) => setTimeout(resolve, 50));

    const RENDER_TIMEOUT_MS = 20000;
    let canvas;
    try {
      canvas = await Promise.race([
        renderToCanvas(element),
        new Promise((_, reject) => setTimeout(() => reject(new Error('html2canvas timeout')), RENDER_TIMEOUT_MS))
      ]);
    } catch (_) {
      canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 794,
        imageTimeout: 4000,
        removeContainer: true,
      });
    }

    if (TEXT_AWARE_SPLIT_DEFAULT) {
      try {
        const SLICE_TIMEOUT_MS = 12000;
        await Promise.race([
          addCanvasAsPagesTextAware(pdf, canvas, element, options),
          new Promise((_, reject) => setTimeout(() => reject(new Error('slice timeout')), SLICE_TIMEOUT_MS))
        ]);
      } catch (_) {
        await addCanvasAsPages(pdf, canvas);
      }
    } else {
      await addCanvasAsPages(pdf, canvas);
    }

    return pdf.output('blob');
  } finally {
    try { document.body.removeChild(element); } catch (_) {}
  }
}

export function buildTravelProductHTMLElement({ contentMainList, itineraryData, productInfo }, options = {}) {
  try {
    const el = createHTMLElement(contentMainList, itineraryData, productInfo);
    try {
      const { hideHeader, hideProductInfo, hideFooter } = options || {};
      if (hideHeader) {
        const n = el.querySelector('[data-section="header"]');
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
      if (hideProductInfo) {
        const n = el.querySelector('[data-section="product-info"]');
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
      if (hideFooter) {
        const n = el.querySelector('[data-section="footer"]');
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
    } catch (_) {}
    return el;
  } catch (e) {
    try { console.error('buildTravelProductHTMLElement error:', e); } catch (_) {}
    return null;
  }
}
